import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { ExternalLink, Save, Heart } from "lucide-react";

const schema = z.object({
  url: z
    .string()
    .trim()
    .url("Must be a valid URL")
    .max(500, "URL too long")
    .refine((v) => v.startsWith("https://"), "URL must start with https://"),
  title: z.string().trim().max(200, "Title too long").optional(),
});

interface PrayerLetterValue {
  url: string;
  title?: string;
  updated_at?: string;
}

const PrayerLetterManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("app_settings")
      .select("value, updated_at")
      .eq("key", "prayer_letter")
      .maybeSingle();
    if (error) {
      toast({ title: "Failed to load", description: error.message, variant: "destructive" });
    } else if (data) {
      const v = (data.value ?? {}) as PrayerLetterValue;
      setUrl(v.url ?? "");
      setTitle(v.title ?? "");
      setUpdatedAt(data.updated_at ?? null);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const parsed = schema.safeParse({ url, title });
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      toast({ title: "Invalid input", description: first.message, variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("app_settings")
      .update({
        value: {
          url: parsed.data.url,
          title: parsed.data.title || "",
          updated_at: new Date().toISOString(),
        },
        updated_at: new Date().toISOString(),
      })
      .eq("key", "prayer_letter");
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Prayer letter updated", description: "The public page now shows the new link." });
    load();
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            Prayer Letter Link
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Update the monthly Prayer Letter link shown on the public Prayer page.
            Paste the Google Slides (or any https) share URL below and click Save.
          </p>

          <div className="space-y-2">
            <Label htmlFor="prayer-title">Title (optional)</Label>
            <Input
              id="prayer-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. OFUESI Monthly Prayer Conference - July 2026"
              disabled={loading || saving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prayer-url">Link URL *</Label>
            <Input
              id="prayer-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://docs.google.com/presentation/d/..."
              disabled={loading || saving}
            />
          </div>

          {updatedAt && (
            <p className="text-xs text-muted-foreground">
              Last updated: {new Date(updatedAt).toLocaleString()}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <Button onClick={save} disabled={loading || saving || !url}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
            {url && (
              <Button variant="outline" asChild>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Preview link
                </a>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrayerLetterManager;
